import {Component, OnInit} from "@angular/core";
import {NgbDateStruct} from "@ng-bootstrap/ng-bootstrap";
import {Company} from "../../shared/api/companies/api.models";
import {AuthenticationService} from "../../shared/authentication.service";
import {MessagesComponent} from "../messages/messages.component";
import {Permission} from "../../shared/api/permissions/api.models";
import * as moment from 'moment';
import {Api} from "../../shared/api.service";
import {StatisticInterface, StatisticResponse} from "../../shared/api/messages/userStatistics/api.models";
import {Observable} from "rxjs/Observable";
import {Observer} from "rxjs/Observer";
import {ActivatedRoute} from "@angular/router";
import {Utils} from "../../shared/utils.service";

class MessageStatisticRow {
    name: string;
    written: number        = 0;
    unapproved: number     = 0;
    unapprovedPercentile: number = 0.0;
    approved: number       = 0;
    approvedPercentile: number = 0.0;
    disapproved: number    = 0;
    disapprovedPercentile: number = 0.0;
    deleted: number        = 0;
    deletedPercentile: number = 0.0;
}

@Component({
    selector: 'statistics-user-performance',
    template: require('./userPerformance.component.html'),
    styles: [require('./userPerformance.component.scss')]
})

export class UserPerformanceComponent implements OnInit {
    static readonly DATE_RANGE_TODAY          = 'Today';
    static readonly DATE_RANGE_YESTERDAY      = 'Yesterday';
    static readonly DATE_RANGE_THIS_WEEK      = 'This week';
    static readonly DATE_RANGE_THIS_MONTH     = 'This month';
    static readonly DATE_RANGE_THIS_YEAR      = 'This year';
    static readonly DATE_RANGE_PREVIOUS_WEEK  = 'Previous week';
    static readonly DATE_RANGE_PREVIOUS_MONTH = 'Previous month';
    static readonly DATE_RANGE_CUSTOM         = 'Custom';

    static readonly SORT_ASC                   = 'ASC';
    static readonly SORT_DESC                  = 'DESC';

    static readonly VIEW_PERCENTILE = 'percentile';
    static readonly VIEW_NUMBERS    = 'numbers';

    protected readonly dateRanges: string[] = [
        UserPerformanceComponent.DATE_RANGE_TODAY,
        UserPerformanceComponent.DATE_RANGE_YESTERDAY,
        UserPerformanceComponent.DATE_RANGE_THIS_WEEK,
        UserPerformanceComponent.DATE_RANGE_THIS_MONTH,
        UserPerformanceComponent.DATE_RANGE_THIS_YEAR,
        UserPerformanceComponent.DATE_RANGE_PREVIOUS_WEEK,
        UserPerformanceComponent.DATE_RANGE_PREVIOUS_MONTH,
        UserPerformanceComponent.DATE_RANGE_CUSTOM
    ];

    protected readonly views: string[] = [
        UserPerformanceComponent.VIEW_PERCENTILE,
        UserPerformanceComponent.VIEW_NUMBERS
    ];

    protected startMinDate: NgbDateStruct;
    protected startMaxDate: NgbDateStruct;
    protected endMinDate: NgbDateStruct;
    protected endMaxDate: NgbDateStruct;

    protected companies: Company[] = [];
    protected isLoading: boolean  = false;
    protected sortColumn: string    = null;
    protected sortDirection: string = null;

    protected showCompanyFilter: boolean = false;
    protected filters: any;
    protected usersHash: any = {};

    protected rows: MessageStatisticRow[] = [];
    protected rowHash: any = {};
    protected statistics: StatisticInterface[] = [];

    protected defaultThresholds: any = {
        'percentile': {
            type : 'percentile',
            value: 50,
            suffix: true
        },
        'numbers'   : {
            type : 'number',
            value: 500,
            suffix: false
        }
    };
    protected currentThreshold: any = this.defaultThresholds.percentile;
    protected currentView: string   = UserPerformanceComponent.VIEW_PERCENTILE;

    constructor(private route: ActivatedRoute, private auth: AuthenticationService, private api: Api, private utils: Utils) {}

    ngOnInit() {
        this.utils.hash(this.usersHash, this.route.snapshot.data['users']);
        this.companies = this.route.snapshot.data['companies'] as Company[];

        this.resetFilters();
        this.reload(true, false);
    }

    startChanges(date: NgbDateStruct) {
        this.endMinDate = date;
        this.filters.dateRangeFrom = date;
        this.reload();
    }

    endChanges(date: NgbDateStruct) {
        this.startMaxDate = date;
        this.filters.dateRangeTill = date;
        this.reload();
    }

    reload(reloadFromApi: boolean = true, reloadFilters: boolean = true) {
        if (reloadFromApi === false) {
            this.populateStatisticTable(this.statistics, reloadFilters);
            return;
        }

        this.isLoading  = true;
        this.statistics = [];
        this.rows    = [];
        this.rowHash = {};

        Observable.create((observer: Observer<any>) => {
            this.loadData(1, observer, []);
        }).subscribe((statistics: StatisticInterface[]) => {
            this.statistics = statistics;
            this.isLoading  = false;

            if (this.filters.isMutated === false) {
                this.resetFilters();
            }

            this.populateStatisticTable(this.statistics, reloadFilters);
        });
    }

    export(event: Event) {
        let lines: any[] = [
            '"' + this.filters.groupBy + '","written","unapproved","approved","disapproved","deleted"'
        ];

        this.rows.forEach((row: MessageStatisticRow) => {
            lines.push(
                '"' +
                [
                    row.name,
                    row.written,
                    this.currentView === UserPerformanceComponent.VIEW_NUMBERS ? row.unapproved : (row.unapprovedPercentile + '%'),
                    this.currentView === UserPerformanceComponent.VIEW_NUMBERS ? row.approved : (row.approvedPercentile + '%'),
                    this.currentView === UserPerformanceComponent.VIEW_NUMBERS ? row.disapproved : (row.disapprovedPercentile + '%'),
                    this.currentView === UserPerformanceComponent.VIEW_NUMBERS ? row.deleted : (row.deletedPercentile + '%')
                ].join('","') +
                '"'
            )
        });

        event.currentTarget['href']     = window.URL.createObjectURL(new Blob([lines.join("\n")], {type : 'text/csv'}));
        event.currentTarget['download'] = 'user_performance_' + moment().valueOf() + '.csv';
    }

    viewChanged() {
        this.currentThreshold = this.defaultThresholds[this.currentView];
    }

    rangeOptionChanges() {
        this.resetCustomDates();
        this.reload();
    }

    filtersMutated(reloadFromApi: boolean = true, reloadFilters: boolean = true) {
        this.filters.isMutated = true;
        this.reload(reloadFromApi, reloadFilters);
    }

    private populateStatisticTable(statistics: StatisticInterface[], reloadFilters: boolean): void {
        this.rows          = [];
        this.rowHash       = {};

        statistics.forEach((statistic: StatisticInterface) => {
            let hashKey = statistic.createdBy;
            let row: MessageStatisticRow;

            if (this.rowHash[hashKey] === undefined) {
                if (this.filters.company && this.filters.company.id !== this.usersHash[statistic.createdBy].companyId) {
                    return;
                }

                row  = new MessageStatisticRow();
                row.name = this.usersHash[statistic.createdBy].username;

                this.rows.push(row);
                this.rowHash[hashKey] = row;
            } else {
                row = this.rowHash[hashKey];
            }

            row.written++;

            if (statistic.approvedDate === null && statistic.disapprovedDate === null) {
                row.unapproved++;
            }

            if (statistic.approvedDate !== null) {
                row.approved++;
            }

            if (statistic.disapprovedDate !== null) {
                row.disapproved++;
            }

            if (statistic.deletedDate !== null) {
                row.deleted++;
            }

            row.unapprovedPercentile  =  Math.round(row.unapproved / row.written * 100);
            row.approvedPercentile     =  Math.round(row.approved / row.written * 100);
            row.disapprovedPercentile =  Math.round(row.disapproved / row.written * 100);
            row.deletedPercentile     =  Math.round(row.deleted / row.written * 100);
        });

        this.sortRows();
    }

    protected toggleSort(column: string) {
        if (column !== this.sortColumn) {
            this.sortColumn    = column;
            this.sortDirection = UserPerformanceComponent.SORT_ASC;

            return this.sortRows();
        }

        if (this.sortDirection === null) {
            this.sortDirection = UserPerformanceComponent.SORT_ASC;
        } else {
            this.sortDirection = (this.sortDirection === UserPerformanceComponent.SORT_DESC) ?
                UserPerformanceComponent.SORT_ASC :
                UserPerformanceComponent.SORT_DESC;
        }

        return this.sortRows();
    }

    protected sortRows() {
        if (this.sortColumn === null) {
            return;
        }

        this.rows.sort((a: MessageStatisticRow, b: MessageStatisticRow) => {
            if (a[this.sortColumn] < b[this.sortColumn]) {
                return this.sortDirection === MessagesComponent.SORT_ASC ? -1 : 1;
            } else if (a[this.sortColumn] > b[this.sortColumn]) {
                return this.sortDirection === MessagesComponent.SORT_ASC ? 1 : -1;
            }

            return 0;
        });
    }

    private loadData(page, mainObserver, statistics: StatisticInterface[] = []) {
        if (page === null) {
            mainObserver.next(statistics);
            mainObserver.complete();

            return;
        }

        let dateRange    = this.getDateRange();

        this.api.messages().userStatistics().collection(
            page,
            5000,
            dateRange[0].set({'hour' : 0, 'minute' : 0, 'second' : 0}).utc().format(),
            dateRange[1].set({'hour' : 23, 'minute' : 59, 'second' : 59}).utc().format()
        ).subscribe((response: StatisticResponse) => {
            response.result.forEach((statistic: StatisticInterface) => statistics.push(statistic));
            this.loadData(response.pagination.next, mainObserver, statistics);
        });
    }

    protected resetFilters(reload: boolean = false) {
        let dateRangeOptions: any = (this.filters) ? {
            dateRangeOption: this.filters.dateRangeOption,
            dateRangeFrom: this.filters.dateRangeFrom,
            dateRangeTill: this.filters.dateRangeTill,
        } : {
            dateRangeOption: MessagesComponent.DATE_RANGE_THIS_WEEK,
            dateRangeFrom: null,
            dateRangeTill: null,
        };

        this.filters = {
            dateRangeOption: dateRangeOptions.dateRangeOption,
            dateRangeFrom: dateRangeOptions.dateRangeFrom,
            dateRangeTill: dateRangeOptions.dateRangeTill,
            company: null,
            isMutated: false
        };

        this.showCompanyFilter = this.auth.canDoAny([Permission.COMPANIES_READ]);

        this.resetCustomDates();

        if (reload === true) {
            this.reload(false, true);
        }
    }

    protected getDateRange(): any[] {
        let from = moment();
        let to   = moment();

        switch (this.filters.dateRangeOption) {
            case MessagesComponent.DATE_RANGE_TODAY:
                break;
            case MessagesComponent.DATE_RANGE_YESTERDAY:
                from.subtract(1, 'days');
                to.subtract(1, 'days');
                break;
            case MessagesComponent.DATE_RANGE_THIS_WEEK:
                from.startOf('isoWeek');
                break;
            case MessagesComponent.DATE_RANGE_THIS_MONTH:
                from.startOf('month');
                break;
            case MessagesComponent.DATE_RANGE_PREVIOUS_WEEK:
                from.startOf('isoWeek').subtract(1, 'days').startOf('isoWeek');
                to.startOf('isoWeek').subtract(1, 'days').endOf('isoWeek');
                break;
            case MessagesComponent.DATE_RANGE_PREVIOUS_MONTH:
                from.startOf('month').subtract(1, 'days').startOf('month');
                to.startOf('month').subtract(1, 'days').endOf('month');
                break;
            case MessagesComponent.DATE_RANGE_THIS_YEAR:
                from.startOf('year').startOf('month');
                break;
            case MessagesComponent.DATE_RANGE_CUSTOM:
                from.set({
                    'year' : this.filters.dateRangeFrom.year,
                    'month' : this.filters.dateRangeFrom.month - 1,
                    'date' : this.filters.dateRangeFrom.day
                });

                to.set({
                    'year' : this.filters.dateRangeTill.year,
                    'month' : this.filters.dateRangeTill.month - 1,
                    'date' : this.filters.dateRangeTill.day
                });

                break;
        }

        from.set({'hour' : 0, 'minute' : 0, 'second' : 0});
        to.set({'hour' : 23, 'minute' : 59, 'second' : 59});

        return [from, to];
    }

    private resetCustomDates() {
        let dateRange = this.getDateRange();

        this.startMinDate = {
            day   : 1,
            month : 3,
            year  : 2018
        };

        this.startMaxDate = {
            day   : dateRange[1].date(),
            month : dateRange[1].month() + 1,
            year  : dateRange[1].year()
        };

        this.endMinDate = {
            day   : dateRange[0].date(),
            month : dateRange[0].month() + 1,
            year  : dateRange[0].year()
        };

        this.endMaxDate = {
            day   : moment().date(),
            month : moment().month() + 1,
            year  : moment().year()
        };

        this.filters.dateRangeFrom = {
            day   : dateRange[0].date(),
            month : dateRange[0].month() + 1,
            year  : dateRange[0].year()
        };

        this.filters.dateRangeTill = {
            day   : dateRange[1].date(),
            month : dateRange[1].month() + 1,
            year  : dateRange[1].year()
        };
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/statistics/userPerformance/userPerformance.component.ts